import { useState, useRef, useEffect } from 'react';
import Loading from '../Loading/Loading';

const Tabs = ({ data, loading }) => {
    // State for the currently active tab index
    const [activeTab, setActiveTab] = useState(0);

    // Ref to the scrollable tab container
    const scrollRef = useRef(null);
    // Array of refs to each tab button, for precise scrolling
    const tabRefs = useRef([]);

    // States controlling left/right arrow visibility
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    /**
     * Updates the visibility of the left/right scroll arrows,
     * based on the current scroll position of the tab container.
     */
    const updateArrows = () => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 2);
        setCanScrollRight(Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth - 2);
    };

    /**
     * Attach scroll and resize listeners to update arrow visibility
     * when the tabs list or window size changes.
     */
    useEffect(() => {
        updateArrows();
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', updateArrows);
        window.addEventListener('resize', updateArrows);
        return () => {
            el.removeEventListener('scroll', updateArrows);
            window.removeEventListener('resize', updateArrows);
        };
    }, [data]);

    /**
     * Trigger a delayed update after rendering tabs.
     * Ensures arrows are correct when new tabs are added.
     */
    useEffect(() => {
        setTimeout(updateArrows, 50);
    }, [data]);

    /**
     * Ensures arrow visibility stays accurate after smooth scrolls.
     */
    useEffect(() => {
        if (!scrollRef.current) return;
        const el = scrollRef.current;
        const onScrollEnd = () => setTimeout(updateArrows, 150);
        el.addEventListener('scroll', onScrollEnd);
        return () => el.removeEventListener('scroll', onScrollEnd);
    }, []);

    /**
     * Scrolls the tabs container left or right and sets the next/previous tab active.
     * @param {'left'|'right'} direction
     */
    const scrollTabs = (direction) => {
        if (scrollRef.current) {
            scrollRef.current.scrollBy({
                left: direction === 'left' ? -120 : 120,
                behavior: 'smooth'
            });
            setTimeout(updateArrows, 200);
        }
        // Set the new active tab index, within bounds
        setActiveTab((prev) => {
            if (direction === 'left') {
                return Math.max(0, prev - 1);
            } else {
                return Math.min(data.length - 1, prev + 1);
            }
        });
    };

    /**
     * Handler for clicking a tab.
     * Scrolls the selected tab into view and sets it active.
     * Special-case: scrolls to far left or right for first/last tab.
     */
    const handleTabClick = (index) => {
        setActiveTab(index);

        if (!scrollRef.current || !tabRefs.current[index]) return;

        // Scroll to edge if first or last tab, else just reveal tab
        if (index === 0) {
            scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
        }
        else if (index === data.length - 1) {
            scrollRef.current.scrollTo({ left: scrollRef.current.scrollWidth, behavior: 'smooth' });
        }
        else {
            tabRefs.current[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
    };

    // --- Render ---
    return (
        <div className="tabs-wrapper">
            {/* Loading state */}
            {loading && <Loading />}

            {/* Tabs navigation bar, scrollable with arrows */}
            {data &&
                <div className="tabs-wrapper__btns" style={{ position: 'relative' }}>
                    {/* Left scroll arrow, shown if content overflows */}
                    {canScrollLeft && (
                        <button
                            className="tabs-arrow tabs-arrow--left"
                            aria-label="Scroll left"
                            type="button"
                            onClick={() => scrollTabs('left')}
                            style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                zIndex: 11,
                                background: '#fff',
                                color: '#000046',
                                border: 'none',
                                fontSize: '20px',
                                cursor: 'pointer',
                                boxShadow: '2px 0 6px rgba(0,0,0,0.06)'
                            }}
                        >&#8249;</button>
                    )}
                    {/* Tab buttons list (scrollable container) */}
                    <div
                        className="tabs-wrapper__btns__inside"
                        ref={scrollRef}
                        style={{
                            overflowX: 'auto',
                            maxWidth: 'fit-content',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none'
                        }}
                    >
                        {data.map((item, index) => (
                            <button
                                key={item.heading}
                                type="button"
                                ref={el => tabRefs.current[index] = el}
                                onClick={() => handleTabClick(index)}
                                className={`tab-heading${activeTab === index ? ' active' : ''}`}
                            >
                                {item.heading}
                            </button>
                        ))}
                    </div>
                    {/* Right scroll arrow, shown if content overflows */}
                    {canScrollRight && (
                        <button
                            className="tabs-arrow tabs-arrow--right"
                            aria-label="Scroll right"
                            type="button"
                            onClick={() => scrollTabs('right')}
                            style={{
                                position: 'absolute',
                                right: 0,
                                top: 0,
                                bottom: 0,
                                zIndex: 11,
                                background: '#fff',
                                color: '#000046',
                                border: 'none',
                                fontSize: '20px',
                                cursor: 'pointer',
                                boxShadow: '-2px 0 6px rgba(0,0,0,0.06)'
                            }}
                        >&#8250;</button>
                    )}
                </div>
            }

            {/* Tab content area, only active tab is shown */}
            {data &&
                <div className="tabs-wrapper__content">
                    <div className="tabs-wrapper__content__inside">
                        {data.map((item, index) =>
                            activeTab === index &&
                            <div
                                key={item.heading}
                                className="tabs-wrapper__content__inside--item"
                            >
                                {item.content}
                            </div>
                        )}
                    </div>
                </div>
            }
        </div>
    );
};

export default Tabs;
